import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  X, // Header/Mobile Menu
  User, Star, Heart, Shield, // Values
  Users, Award, BookOpen, Target, CheckCircle, Home, // Why Choose Us
} from 'lucide-react';
import { asset } from '../../../shared/utils/asset';
import { SEO } from '../../../shared/components';

// Mock asset function: Since we are in a single file, we cannot use '../../../shared/utils/asset'. 
// We will use a function that returns the path directly, assuming the assets folder is accessible.

// --- Reusable Component for Value Cards (Since the styling is unique here) ---
interface ValueCardProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    className?: string;
}

const ValueCard: React.FC<ValueCardProps> = ({ icon, title, description, className = "" }) => (
    <div className={`bg-white p-8 rounded-xl shadow-lg text-center transition-shadow duration-300 hover:shadow-xl ${className}`}>
        <div className="w-16 h-16 bg-cyan-100 rounded-xl flex items-center justify-center mx-auto mb-6">
            {icon}
        </div>
        <h3 className="text-xl font-display font-bold text-foreground mb-3">{title}</h3>
        <p className="text-muted-foreground">{description}</p>
    </div>
);


const Aboutpage: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // Toggle logic for mobile menu
    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
        document.body.classList.toggle('overflow-hidden', !isMenuOpen);
    };

    // Data for Values Section
    const coreValues = [
        { icon: <User className="w-8 h-8 text-accent" />, title: 'Student-Centered', description: 'We place students at the heart of everything we do, tailoring our approach to meet their unique needs.' },
        { icon: <Star className="w-8 h-8 text-accent" />, title: 'Excellence', description: 'We strive for excellence in all aspects of our work, constantly seeking to improve our teaching methods.' },
        { icon: <Heart className="w-8 h-8 text-accent" />, title: 'Compassion', description: 'We approach education with empathy and understanding, supporting students through challenges.' },
        { icon: <Shield className="w-8 h-8 text-accent" />, title: 'Integrity', description: 'We uphold the highest standards of professionalism, honesty, and ethical conduct in all interactions.' },
    ];

    // Data for Why Choose Us Section
    const whyChooseUsLeft = [
        { icon: <Users className="w-6 h-6 text-accent" />, title: 'Expert Teachers', description: 'Our team consists of highly qualified educators with extensive experience and a passion for teaching.' },
        { icon: <Target className="w-6 h-6 text-accent" />, title: 'Tailored Learning', description: 'We create individualized learning plans that address each student\'s specific needs and goals.' },
        { icon: <BookOpen className="w-6 h-6 text-accent" />, title: 'Comprehensive Support', description: 'We offer a wide range of subjects and specialized programs to meet diverse educational needs.' },
    ];
    
    const whyChooseUsRight = [
        { icon: <CheckCircle className="w-6 h-6 text-accent" />, title: 'Proven Results', description: 'Our students consistently achieve excellent academic results and gain admission to top schools.' },
        { icon: <Home className="w-6 h-6 text-accent" />, title: 'Supportive Environment', description: 'We foster a positive and encouraging atmosphere where students feel comfortable taking academic risks.' },
        { icon: <Award className="w-6 h-6 text-accent" />, title: 'Licensed & Recognized', description: 'We are officially licensed by the Ministry of Education & Higher Education in Qatar.' },
    ];


    return (
        <div className="bg-accent text-white min-h-screen">
            <SEO
                title="About Tuition Point - Licensed Education Center in Al Wakra, Qatar"
                description="Learn about Tuition Point, a Ministry-licensed education center in Al Wakra, Qatar. Our qualified educators provide individualized learning for K-12 students across multiple curricula including CBSE, IGCSE, and IB."
                keywords="education center Al Wakra, licensed tuition Qatar, qualified tutors Qatar, individualized learning Qatar, Ministry approved tuition, about tuition center, education services Qatar, Al Wakra tutoring"
            />

           
            
            {/* Mobile Menu Overlay */}
            <div className={`fixed inset-0 z-50 bg-white transition-transform duration-300 ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                 <div className="container mx-auto px-4 h-full flex flex-col py-8 max-w-7xl">
                    <div className="flex justify-between items-center mb-12">
                        <Link to="/" className="flex items-center space-x-2">
                            <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-white">
                                <span className="font-display font-bold text-lg">TP</span>
                            </div>
                            <span className="font-display font-bold text-primary text-lg">Tuition Point</span>
                        </Link>
                        <button onClick={toggleMenu} aria-label="Close Menu">
                            <X className="w-6 h-6 text-primary" />
                        </button>
                    </div>
                    <nav className="flex flex-col space-y-6 text-xl">
                        <Link to="/" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Home</Link>
                        <Link to="/about" onClick={toggleMenu} className="nav-link text-accent font-semibold">About Us</Link>
                        <Link to="/services" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Services</Link>
                        <Link to="/team" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Our Team</Link>
                        <Link to="/achievements" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Success Stories</Link>
                        <Link to="/contact" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Contact</Link>
                        <Link to="/enrollment" onClick={toggleMenu} className="btn-primary w-full text-center mt-4">Enroll Now</Link>
                    </nav>
                </div>
            </div>
            
            {/* --------------------- Page Header (Header is already centered) --------------------- */}
            <section className="pt-16 pb-12 md:pt-20 md:pb-16 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-3xl mx-auto">
                        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 animate-slide-up">About Tuition Point - Al Wakra's Trusted Education Center</h1>
                        <p className="text-lg text-white animate-slide-up">Officially licensed by the Ministry of Education and Higher Education in Qatar, we deliver personalized, results-driven tutoring for K–12 students led by qualified educators.</p>
                    </div>
                </div>
            </section>
            
            {/* --------------------- About Section (Our Story) --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="bg-white p-8 rounded-xl shadow-lg animate-slide-up">
                            <h2 className="text-4xl font-display font-semibold text-foreground mb-6">Our Story - Education Excellence in Al Wakra</h2>
                            <p className="text-lg text-muted-foreground mb-6">
                                Tuition Point Education Center was established in Al Wakra, Qatar with a clear vision: to provide high-quality, personalized tutoring that helps students reach their full potential. We serve students from kindergarten to grade 12 across CBSE, IGCSE, IB, Federal, and Bangladesh Board curricula.
                            </p>
                            <p className="text-lg text-muted-foreground mb-6">
                                Our center began with a handful of qualified, experienced teachers and a small group of dedicated students. Through our commitment to excellence and personalized approach to tutoring, we've grown into Al Wakra's trusted education center serving hundreds of students across Qatar.
                            </p>
                            <p className="text-lg text-muted-foreground">
                                Tuition Point is officially licensed by the Ministry of Education & Higher Education in Qatar. Our experienced tutors hold M.Sc., B.Ed., and advanced degrees, specializing in CBSE, IGCSE, IB board exam preparation, entrance test coaching (IIT-JEE, NEET), and comprehensive K-12 subject tutoring.
                            </p>
                        </div>
                        <div className="relative animate-slide-up">
                            <img
                                src={asset('About/about_banner.jpg')}
                                alt="Students studying at Tuition Point"
                                className="w-full h-auto rounded-2xl shadow-xl relative z-10"
                            />
                        </div>
                    </div>
                </div>
            </section>
            
            {/* --------------------- Mission & Vision --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Card 1: Mission */}
                        <div className="bg-white p-10 rounded-xl shadow-lg animate-slide-up">
                            <h2 className="text-2xl font-display font-bold text-foreground mb-6">Our Mission - Quality Education for All</h2>
                            <p className="text-base text-muted-foreground mb-6">
                                Our mission is to empower K-12 students in Al Wakra and across Qatar with the knowledge, skills, and confidence necessary to excel academically. We create a supportive tutoring environment where each student can discover their potential through personalized learning plans tailored to CBSE, IGCSE, IB, and other curricula.
                            </p>
                            <p className="text-base text-muted-foreground">
                                We are committed to providing individualized education that addresses each student's unique learning style and needs, helping them overcome academic challenges and achieve excellent results in board exams and entrance tests.
                            </p>
                        </div>

                        {/* Card 2: Vision */}
                        <div className="bg-white p-10 rounded-xl shadow-lg animate-slide-up">
                            <h2 className="text-2xl font-display font-bold text-foreground mb-6">Our Vision - Leading Education Center in Qatar</h2>
                            <p className="text-base text-muted-foreground mb-6">
                                Our vision is to be Al Wakra and Qatar's leading tutoring center that transforms lives through quality education. We aim to be recognized for our expert teachers, innovative teaching methods, proven results, and the outstanding academic success of our students in board exams and school admissions.
                            </p>
                            <p className="text-base text-muted-foreground">
                                We envision every student who enrolls at Tuition Point to be equipped with strong academic foundations, excellent problem-solving abilities, and the confidence needed to excel in CBSE, IGCSE, IB examinations and achieve admission to top schools like BPS and DPS.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* --------------------- Values --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4">Our Core Values</h2>
                        <p className="text-lg text-white/80">These principles guide everything we do at Tuition Point.</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {coreValues.map((value, index) => (
                            <ValueCard
                                key={index}
                                icon={value.icon}
                                title={value.title}
                                description={value.description}
                                className={`animate-slide-up animation-delay-${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </section>
            
            {/* --------------------- Why Choose Us (Detailed) --------------------- */}
            <section className="py-24 bg-accent">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
                        <h2 className="text-4xl font-display font-semibold text-white mb-4">Why Choose Tuition Point?</h2>
                        <p className="text-lg text-white/80">Discover what sets us apart from other educational centers.</p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">

                        {/* Column 1: Left Features */}
                        <div className="space-y-8 animate-slide-up">
                            {whyChooseUsLeft.map((item, index) => (
                                <div key={index} className="bg-white p-6 rounded-xl shadow-lg flex items-start gap-4">
                                    <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                                        {item.icon}
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-display font-bold text-foreground mb-2">{item.title}</h3>
                                        <p className="text-muted-foreground">{item.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Column 2: Center Image */}
                        <div className="relative mx-auto max-w-sm lg:max-w-none animate-fade-in">
                            <img
                                src={asset('About/about2_banner.jpg')}
                                alt="Tuition Point learning environment"
                                className="relative rounded-2xl shadow-xl w-full h-auto"
                            />
                        </div>

                        {/* Column 3: Right Features */}
                        <div className="space-y-8 animate-slide-up">
                            {whyChooseUsRight.map((item, index) => (
                                <div key={index} className="bg-white p-6 rounded-xl shadow-lg flex items-start gap-4">
                                    <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                                        {item.icon}
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-display font-bold text-foreground mb-2">{item.title}</h3>
                                        <p className="text-muted-foreground">{item.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
            
            {/* --------------------- CTA --------------------- */}
            <section className="py-24 bg-accent text-white">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-3xl md:text-4xl font-display font-semibold mb-6 animate-slide-up">Ready to Start Your Educational Journey?</h2>
                        <p className="text-lg mb-10 text-white text-opacity-90 animate-slide-up">Take the first step toward academic excellence with Tuition Point Education Center.</p>
                       <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
                                     {/* UPDATE: Applied explicit Tailwind classes for buttons */}
                                     <Link 
                                       to="/enrollment" 
                                       className="bg-white text-accent hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg shadow-xl transition duration-300  sm:w-auto text-lg"
                                     >
                                       Enroll Today
                                     </Link>
                                     <Link 
                                       to="/contact" 
                                       className="border-2 border-white text-white hover:bg-white hover:text-accent font-semibold py-3 px-8 rounded-lg transition duration-300  sm:w-auto text-lg"
                                     >
                                       Contact Us
                                     </Link>
                                   </div>
                    </div>
                </div>
            </section>
            
            
        </div>
    );
};

export default Aboutpage;
